import { useTranslation } from 'react-i18next';

import { changeLanguage, getCurrentLanguage, isRTL } from '@/shared/config/i18n.config';

/**
 * What `t` actually returns for this codebase.
 *
 * react-i18next v11 types `TFunction` as returning `string | object | Array | null |
 * undefined`, because i18next can be asked for objects and arrays via `returnObjects`.
 * Nothing here does that, and a missing key returns the key itself rather than nothing —
 * so every call site gets a string. Callers were paying for the wider type in `?? ''`
 * patches and unassignable-prop errors on labels and placeholders.
 */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const useLocalization = () => {
  const { t: translate, i18n } = useTranslation();
  const t = translate as TranslateFn;

  const setLanguage = (language: 'en' | 'ar') => {
    changeLanguage(language);
  };

  return {
    t,
    i18n,
    currentLanguage: getCurrentLanguage(),
    isRTL: isRTL(),
    changeLanguage,
    setLanguage,
    // Direct RTL check for convenience
    isRTLDirect: i18n.language === 'ar',
    // Common translations
    common: {
      actions: {
        add: () => t('SHARED.COMMON.ACTIONS.ADD'),
        edit: () => t('SHARED.COMMON.ACTIONS.EDIT'),
        delete: () => t('SHARED.COMMON.ACTIONS.DELETE'),
        save: () => t('SHARED.COMMON.ACTIONS.SAVE'),
        cancel: () => t('SHARED.COMMON.ACTIONS.CANCEL'),
        confirm: () => t('SHARED.COMMON.ACTIONS.CONFIRM'),
        back: () => t('SHARED.COMMON.ACTIONS.BACK'),
        next: () => t('SHARED.COMMON.ACTIONS.NEXT'),
        finish: () => t('SHARED.COMMON.ACTIONS.FINISH'),
        submit: () => t('SHARED.COMMON.ACTIONS.SUBMIT'),
        reset: () => t('SHARED.COMMON.ACTIONS.RESET'),
        search: () => t('SHARED.COMMON.ACTIONS.SEARCH'),
        filter: () => t('SHARED.COMMON.ACTIONS.FILTER'),
        export: () => t('SHARED.COMMON.ACTIONS.EXPORT'),
        import: () => t('SHARED.COMMON.ACTIONS.IMPORT'),
        view: () => t('SHARED.COMMON.ACTIONS.VIEW'),
        details: () => t('SHARED.COMMON.ACTIONS.DETAILS'),
        all: () => t('SHARED.COMMON.ACTIONS.ALL')
      },
      status: {
        active: () => t('SHARED.COMMON.STATUS.ACTIVE'),
        inactive: () => t('SHARED.COMMON.STATUS.INACTIVE'),
        pending: () => t('SHARED.COMMON.STATUS.PENDING'),
        approved: () => t('SHARED.COMMON.STATUS.APPROVED'),
        rejected: () => t('SHARED.COMMON.STATUS.REJECTED'),
        completed: () => t('SHARED.COMMON.STATUS.COMPLETED'),
        cancelled: () => t('SHARED.COMMON.STATUS.CANCELLED')
      },
      messages: {
        loading: () => t('SHARED.COMMON.MESSAGES.LOADING'),
        noData: () => t('SHARED.COMMON.MESSAGES.NO_DATA'),
        error: () => t('SHARED.COMMON.MESSAGES.ERROR'),
        success: () => t('SHARED.COMMON.MESSAGES.SUCCESS'),
        warning: () => t('SHARED.COMMON.MESSAGES.WARNING'),
        info: () => t('SHARED.COMMON.MESSAGES.INFO')
      },
      buttons: {
        apply: () => t('SHARED.COMMON.BUTTONS.APPLY'),
        recover: () => t('SHARED.COMMON.BUTTONS.RECOVER'),
        download: () => t('SHARED.COMMON.BUTTONS.DOWNLOAD'),
        preview: () => t('SHARED.COMMON.BUTTONS.PREVIEW'),
        selectAll: () => t('SHARED.COMMON.BUTTONS.SELECT_ALL'),
        addNew: () => t('SHARED.COMMON.BUTTONS.ADD_NEW'),
        addAnother: () => t('SHARED.COMMON.BUTTONS.ADD_ANOTHER'),
        saveChanges: () => t('SHARED.COMMON.BUTTONS.SAVE_CHANGES'),
        deletePermanently: () => t('SHARED.COMMON.BUTTONS.DELETE_PERMANENTLY'),
        deleteTeam: () => t('SHARED.COMMON.BUTTONS.DELETE_TEAM'),
        assignTo: () => t('SHARED.COMMON.BUTTONS.ASSIGN_TO'),
        unassign: () => t('SHARED.COMMON.BUTTONS.UNASSIGN'),
        internalTransfer: () => t('SHARED.COMMON.BUTTONS.INTERNAL_TRANSFER'),
        generateReport: () => t('SHARED.COMMON.BUTTONS.GENERATE_REPORT')
      },
      formModal: {
        deleteConfirmation: (action: string) =>
          t('SHARED.COMMON.FORM_MODAL.DELETE_CONFIRMATION', { action })
      },
      uploader: {
        dragOrBrowse: () => t('SHARED.COMMON.UPLOADER.DRAG_OR_BROWSE'),
        browse: () => t('SHARED.COMMON.UPLOADER.BROWSE'),
        uploading: () => t('SHARED.COMMON.UPLOADER.UPLOADING'),
        unsupportedFileType: (types: string) =>
          t('SHARED.COMMON.UPLOADER.UNSUPPORTED_FILE_TYPE', { types })
      },
      titles: {
        basicDetails: () => t('SHARED.COMMON.TITLES.BASIC_DETAILS'),
        residentialDetails: () => t('SHARED.COMMON.TITLES.RESIDENTIAL_DETAILS'),
        referenceDetails: () => t('SHARED.COMMON.TITLES.REFERENCE_DETAILS'),
        incomeDetails: () => t('SHARED.COMMON.TITLES.INCOME_DETAILS'),
        jobDetails: () => t('SHARED.COMMON.TITLES.JOB_DETAILS'),
        freelanceDetails: () => t('SHARED.COMMON.TITLES.FREELANCE_DETAILS'),
        carDetails: () => t('SHARED.COMMON.TITLES.CAR_DETAILS'),
        productDetails: () => t('SHARED.COMMON.TITLES.PRODUCT_DETAILS'),
        financeDetails: () => t('SHARED.COMMON.TITLES.FINANCE_DETAILS'),
        applicationPreview: () => t('SHARED.COMMON.TITLES.APPLICATION_PREVIEW'),
        commercialRegistration: () => t('SHARED.COMMON.TITLES.COMMERCIAL_REGISTRATION'),
        taxDetails: () => t('SHARED.COMMON.TITLES.TAX_DETAILS'),
        bankAccount: () => t('SHARED.COMMON.TITLES.BANK_ACCOUNT'),
        standardFeatures: () => t('SHARED.COMMON.TITLES.STANDARD_FEATURES'),
        carHighlights: () => t('SHARED.COMMON.TITLES.CAR_HIGHLIGHTS'),
        dimensions: () => t('SHARED.COMMON.TITLES.DIMENSIONS'),
        exteriorFeatures: () => t('SHARED.COMMON.TITLES.EXTERIOR_FEATURES'),
        interiorFeatures: () => t('SHARED.COMMON.TITLES.INTERIOR_FEATURES'),
        safety: () => t('SHARED.COMMON.TITLES.SAFETY'),
        tenors: () => t('SHARED.COMMON.TITLES.TENORS'),
        eligibleBrands: () => t('SHARED.COMMON.TITLES.ELIGIBLE_BRANDS'),
        productFeatures: () => t('SHARED.COMMON.TITLES.PRODUCT_FEATURES'),
        archivedTeam: () => t('SHARED.COMMON.TITLES.ARCHIVED_TEAM'),
        performanceOverview: () => t('SHARED.COMMON.TITLES.PERFORMANCE_OVERVIEW'),
        totalLeads: () => t('SHARED.COMMON.TITLES.TOTAL_LEADS'),
        open: () => t('SHARED.COMMON.TITLES.OPEN'),
        reopened: () => t('SHARED.COMMON.TITLES.REOPENED'),
        pending: () => t('SHARED.COMMON.TITLES.PENDING'),
        duplicate: () => t('SHARED.COMMON.TITLES.DUPLICATE'),
        notReachable: () => t('SHARED.COMMON.TITLES.NOT_REACHABLE'),
        totalFinancedAmount: () => t('SHARED.COMMON.TITLES.TOTAL_FINANCED_AMOUNT'),
        totalApplicationsCount: () => t('SHARED.COMMON.TITLES.TOTAL_APPLICATIONS_COUNT')
      },
      tooltips: {
        preview: () => t('SHARED.COMMON.TOOLTIPS.PREVIEW'),
        download: () => t('SHARED.COMMON.TOOLTIPS.DOWNLOAD'),
        delete: () => t('SHARED.COMMON.TOOLTIPS.DELETE'),
        editAssignedAgent: () => t('SHARED.COMMON.TOOLTIPS.EDIT_ASSIGNED_AGENT'),
        editDate: () => t('SHARED.COMMON.TOOLTIPS.EDIT_DATE'),
        deleteTeamWarning: () => t('SHARED.COMMON.TOOLTIPS.DELETE_TEAM_WARNING')
      },
      table: {
        columns: {
          actions: () => t('SHARED.COMMON.TABLE.COLUMNS.ACTIONS'),
          status: () => t('SHARED.COMMON.TABLE.COLUMNS.STATUS'),
          source: () => t('SHARED.COMMON.TABLE.COLUMNS.SOURCE'),
          view: () => t('SHARED.COMMON.TABLE.COLUMNS.VIEW'),
          edit: () => t('SHARED.COMMON.TABLE.COLUMNS.EDIT'),
          name: () => t('SHARED.COMMON.TABLE.COLUMNS.NAME'),
          role: () => t('SHARED.COMMON.TABLE.COLUMNS.ROLE'),
          reportsTo: () => t('SHARED.COMMON.TABLE.COLUMNS.REPORTS_TO'),
          commission: () => t('SHARED.COMMON.TABLE.COLUMNS.COMMISSION'),
          performance: () => t('SHARED.COMMON.TABLE.COLUMNS.PERFORMANCE'),
          team: () => t('SHARED.COMMON.TABLE.COLUMNS.TEAM')
        },
        pagination: {
          showing: () => t('SHARED.COMMON.TABLE.PAGINATION.SHOWING'),
          from: () => t('SHARED.COMMON.TABLE.PAGINATION.FROM'),
          record: () => t('SHARED.COMMON.TABLE.PAGINATION.RECORD'),
          records: () => t('SHARED.COMMON.TABLE.PAGINATION.RECORDS')
        },
        commonColumns: {
          id: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.ID'),
          lastUpdatedAt: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.LAST_UPDATED_AT'),
          customer: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.CUSTOMER'),
          financedAmount: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.FINANCED_AMOUNT'),
          financeMerchant: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.FINANCE_MERCHANT'),
          autoMerchant: () => t('SHARED.COMMON.TABLE.COMMON_COLUMNS.AUTO_MERCHANT')
        },
        sideMenu: {
          dashboard: () => t('SIDE_MENU.DASHBOARD'),
          financeNow: () => t('SIDE_MENU.FINANCE_NOW'),
          communication: () => t('SIDE_MENU.COMMUNICATION'),
          whatsapp: () => t('SIDE_MENU.WHATSAPP'),
          corporateWhatsapp: () => t('SIDE_MENU.CORPORATE_WHATSAPP'),
          operations: () => t('SIDE_MENU.OPERATIONS'),
          transactions: () => t('SIDE_MENU.TRANSACTIONS'),
          purchaseOrders: () => t('SIDE_MENU.PURCHASE_ORDERS'),
          leads: () => t('SIDE_MENU.LEADS'),
          applicationsDashboard: () => t('SIDE_MENU.APPLICATIONS_DASHBOARD'),
          profiles: () => t('SIDE_MENU.PROFILES'),
          financeApplications: () => t('SIDE_MENU.FINANCE_APPLICATIONS'),
          customers: () => t('SIDE_MENU.CUSTOMERS'),
          partners: () => t('SIDE_MENU.PARTNERS'),
          merchants: () => t('SIDE_MENU.MERCHANTS'),
          referrals: () => t('SIDE_MENU.REFERRALS'),
          products: () => t('SIDE_MENU.PRODUCTS'),
          autoProducts: () => t('SIDE_MENU.AUTO_PRODUCTS'),
          financialProducts: () => t('SIDE_MENU.FINANCIAL_PRODUCTS'),
          reports: () => t('SIDE_MENU.REPORTS'),
          users: () => t('SIDE_MENU.USERS'),
          teams: () => t('SIDE_MENU.TEAMS'),
          commissionScheme: () => t('SIDE_MENU.COMMISSION_SCHEME'),
          archive: () => t('SIDE_MENU.ARCHIVE'),
          autoBrands: () => t('SIDE_MENU.AUTO_BRANDS'),
          sales: () => t('SIDE_MENU.SALES'),
          settings: () => t('SIDE_MENU.SETTINGS'),
          accounts: () => t('SIDE_MENU.ACCOUNTS'),
          wallets: () => t('SIDE_MENU.WALLETS')
        }
      }
    }
  };
};
